import React, { useState, useEffect } from 'react';
import Breadcrumbs from '../common/Breadcrumbs';
import Icon from '../common/Icon';
import TimeStamp from '../common/TimeStamp';
import Row from '../common/Row';
import { Marked } from '../common/Marked';
import {
  getCalendarField,
  getToField,
  handleSendTestMailer,
  previewTokenNames,
} from './constants/mailerConstants';
import { InvalidBodyFields } from './mailer-body-inject/InvalidBodyFields';
import { MailerContext } from '../../hooks/Contexts';
import MailerEdit from './MailerEdit';
import { useLoaderData, useNavigate } from 'react-router-dom';
import { handleDestroyMailer } from './constants/handleMailer';
import { notify } from '../../helpers/notifications';

const MailerShow = () => {
  const { data } = useLoaderData();
  const navigate = useNavigate();
  const { container, form, fieldsets, metadata_entries, related_forms } = data;
  const [mailer, setMailer] = useState(data.mailer);
  const [isEditing, setIsEditing] = useState(false);
  const invalidIds = mailer.scan_for_invalid_bodies;
  const mailerInvalid = invalidIds?.body_is_invalid;
  const { field_id, metadata_entry_id, calendar_event_field_id } = mailer;

  const toggleEdit = (e) => {
    e ? e.preventDefault() : null;
    setIsEditing(!isEditing);
  };

  useEffect(() => {
    if (mailerInvalid) {
      notify.alertFailure('Attention!', 'Please fix mailer references.');
    } else {
      notify.success('Scan successful', { showOnlyTheLastOne: false });
    }
  }, [mailer]);

  const to = getToField(
    field_id,
    metadata_entry_id,
    fieldsets,
    metadata_entries
  );

  const calendarField = getCalendarField(calendar_event_field_id, fieldsets);

  const context = {
    mailer,
    setMailer,
    fieldsets,
    metadata_entries,
    form,
    related_forms,
    invalidIds,
    mailerInvalid,
  };

  const destroyMailer = async () => {
    await handleDestroyMailer(mailer.id, (response) => {
      if (response.ok) navigate(`/manage/forms/${form.id}/mailers`);
    });
  };

  const handleClose = (e) => {
    e.preventDefault();
    navigate(`/manage/forms/${form.id}/mailers`);
  };

  return (
    <MailerContext.Provider value={context}>
      <Breadcrumbs
        form={{ name: form.name, id: form.id }}
        container={{ name: container.name, id: container.id }}
      />

      <div className='mailer'>
        {isEditing ? (
          <MailerEdit toggleEdit={toggleEdit} />
        ) : (
          <div className='details'>
            <div className='controls'>
              <button onClick={(e) => handleClose(e)} className='blue'>
                Close
                <Icon icon='xmark' />
              </button>
              <Icon
                icon='mail-reply'
                title='Test mailer'
                className='test'
                onClick={() => handleSendTestMailer(mailer)}
              />
              <Icon icon='pencil' className='edit' onClick={toggleEdit} />
              <Icon icon='trash' className='destroy' onClick={destroyMailer} />
            </div>

            {mailerInvalid && <InvalidBodyFields invalidIds={invalidIds} />}

            <Row name='To field:'>
              {to.fieldset} <i className='fa-solid fa-caret-right' />
              {' ' + to.field + ' '}
              {to.meta && (
                <>
                  <i className='fa-solid fa-caret-right' /> {to.meta}
                </>
              )}
            </Row>

            <Row name='Name:'>{mailer.name}</Row>

            <Row name='On Submit:'>{mailer.on_submit ? 'Yes' : 'No'}</Row>

            <Row name='Allow Universal Sending?'>
              {mailer.universal_mail_send ? 'Yes' : 'No'}
            </Row>

            <Row name='From:'>{mailer.from ? mailer.from : 'default'}</Row>

            <Row name='Subject:'>{mailer.subject}</Row>

            <Row name='Calendar Field:'>{calendarField?.name || 'None'}</Row>

            <Row name='Body:'>
              <Marked
                text={previewTokenNames(mailer.body, fieldsets, related_forms)}
              />
            </Row>

            <Row name='PDF Body:'>
              <Marked
                text={previewTokenNames(
                  mailer.pdf_body,
                  fieldsets,
                  related_forms
                )}
              />
            </Row>

            <Row name='Landscape:'>{mailer.landscape ? 'Yes' : 'No'}</Row>
          </div>
        )}
        <div className='form actions'>
          <div className='left'>
            <TimeStamp title='Created' value={mailer.created_at} isHeading />
            <TimeStamp title='Updated' value={mailer.updated_at} isHeading />
          </div>

          <div className='right controls'>
            <button onClick={(e) => handleClose(e)} className='blue'>
              Return to Mailers
            </button>
          </div>
        </div>
      </div>
      <div id='context-menu-wrapper' />
    </MailerContext.Provider>
  );
};

export default MailerShow;
